import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { camelCase } from '@exonet/jsonapi-data-collections';
import { formatPrice } from '@exonet/jsonapi-data-collections/lib/utilities';

export const SEARCH_TYPES = {
  tickets: {
    tabKey: 'tickets',
    permissionGroup: 'tickets.read',
    titleKey: 'search:common.tabs.tickets',
    icon: byPrefixAndName.fas['envelope-open'],
    fields: ['number', 'owner', 'last_email_subject', 'last_email_body', 'last_email_author'],

    optionBuilder: (ticket, t) => {
      const statusKey = (ticket.getStatus() ?? '').toLowerCase();

      const statusVariantByKey = {
        open: 'success',
        closed: 'muted',
      };

      const lastMessageDate = ticket.getLastMessageDate();

      return {
        id: ticket.id(),
        title: ticket.getTitle(),
        subtitle: [
          {
            type: 'status',
            variant: statusVariantByKey[statusKey] ?? 'muted',
            label: t(`search:tickets.${statusKey}`),
          },
          ...(lastMessageDate
            ? [
                {
                  type: 'date',
                  label: `${t('search:tickets.lastActivity')} ${lastMessageDate.asShortDateTime()}`,
                },
              ]
            : []),
        ],
        link: `/tickets/${ticket.id()}`,
      };
    },
  },

  domains: {
    tabKey: 'domains',
    permissionGroup: 'domains.read',
    titleKey: 'search:common.tabs.domains',
    icon: byPrefixAndName.fas.browser,
    fields: ['name', 'owner_handle', 'admin_handle'],

    optionBuilder: (domain, t) => {
      const statusKey = (domain.getStatus() ?? 'unknown').toLowerCase();

      const statusVariantByKey = {
        active: 'success',
        pending: 'warning',
        failed: 'danger',
      };

      const statusLabel = t(`search:domains.${camelCase(statusKey)}`, statusKey.replace('_', ' '));

      return {
        id: domain.id(),
        title: domain.getName(),
        subtitle: [
          {
            type: 'status',
            variant: statusVariantByKey[statusKey] ?? 'muted',
            label: statusLabel,
          },
        ],
        link: `/domains/${domain.id()}`,
      };
    },
  },

  certificates: {
    tabKey: 'certificates',
    permissionGroup: 'certificates.read',
    titleKey: 'search:common.tabs.certificates',
    icon: byPrefixAndName.fas['shield-check'],
    fields: ['domain', 'uuid'],

    optionBuilder: (certificate, t) => {
      const statusKey = (certificate.getStatus() ?? 'unknown').toLowerCase();

      const statusVariantByKey = {
        active: 'success',
        pending: 'warning',
        expired: 'muted',
        revoked: 'danger',
      };

      const statusLabel = t(
        `search:certificates.${camelCase(statusKey)}`,
        statusKey.replace('_', ' '),
      );

      return {
        id: certificate.id(),
        title: certificate.getDomain(),
        subtitle: [
          {
            type: 'status',
            variant: statusVariantByKey[statusKey] ?? 'muted',
            label: statusLabel,
          },
          {
            type: 'text',
            label: certificate.getProduct(),
          },
        ],
        link: `/certificates/${certificate.id()}`,
      };
    },
  },

  dns_zones: {
    tabKey: 'dns',
    permissionGroup: 'dns.read',
    titleKey: 'search:common.tabs.dns',
    icon: byPrefixAndName.fas.globe,
    fields: ['name'],

    optionBuilder: (zone, t) => {
      const hasDomain = zone.hasDomainRelation();

      return {
        id: zone.id(),
        title: zone.getName(),
        subtitle: [
          {
            type: 'status',
            variant: hasDomain ? 'success' : 'danger',
            label: hasDomain ? t('search:dns.registered') : t('search:dns.unregistered'),
          },
        ],
        link: `/dns/${zone.id()}`,
      };
    },
  },

  virtual_servers: {
    tabKey: 'servers',
    permissionGroup: 'servers.read',
    titleKey: 'search:common.tabs.servers',
    icon: byPrefixAndName.fas.server,
    fields: ['uuid', 'hostname', 'name', 'main_ipv4_address.raw', 'main_ipv6_address.raw'],

    optionBuilder: (server) => {
      const ipLabel = server.getMainIpv4Address() || server.getMainIpv6Address();

      return {
        id: server.id(),
        title: server.getDisplayName(),
        subtitle: [...(ipLabel ? [{ type: 'text', label: ipLabel }] : [])],
        link: `/servers/${server.id()}`,
      };
    },
  },

  managed_service_apps: {
    tabKey: 'apps',
    permissionGroup: 'apps.read',
    titleKey: 'search:common.tabs.apps',
    icon: byPrefixAndName.fas['grid-round'],
    fields: ['name', 'projects', 'organisation'],

    optionBuilder: (app) => {
      const organisationLabel = app.getOrganisation();

      return {
        id: app.id(),
        title: app.getName(),
        subtitle: [...(organisationLabel ? [{ type: 'text', label: organisationLabel }] : [])],
        link: `/apps/${app.id()}`,
      };
    },
  },

  customer_products: {
    tabKey: 'products',
    permissionGroup: 'products.read',
    titleKey: 'search:common.tabs.products',
    icon: byPrefixAndName.fas['boxes-stacked'],
    fields: ['name', 'description'],

    optionBuilder: (product, t) => {
      const cancellationDate = product.getCancellationDate?.();
      const isCancelled = Boolean(cancellationDate);

      const statusKey = isCancelled ? 'cancelled' : 'active';
      const statusVariantByKey = {
        active: 'success',
        cancelled: 'muted',
      };

      const quantity = product.getQuantity?.();

      const totalPrice = quantity === null ? product.getPrice?.() : product.getTotalPrice?.();

      const totalLabel = typeof totalPrice === 'number' ? formatPrice(totalPrice) : null;

      return {
        id: product.id(),
        title: product.getName?.() || product.getDisplayName?.(),
        subtitle: [
          {
            type: 'status',
            variant: statusVariantByKey[statusKey] ?? 'muted',
            label: t(`search:products.${statusKey}`, statusKey),
          },
          ...(totalLabel
            ? [
                {
                  type: 'text',
                  label: totalLabel,
                },
              ]
            : []),
          ...(cancellationDate
            ? [
                {
                  type: 'date',
                  label: `${t('search:products.cancellationDate')} ${cancellationDate.asDate?.()}`,
                },
              ]
            : []),
        ],
        link: `/products?filter[id]=${product.id()}`,
      };
    },
  },

  invoices: {
    tabKey: 'invoices',
    permissionGroup: 'invoices.read',
    titleKey: 'search:common.tabs.invoices',
    icon: byPrefixAndName.fas['file-invoice-dollar'],
    fields: ['number', 'payment_reference'],

    optionBuilder: (invoice, t) => {
      const statusKey = invoice.getStatus().toLowerCase();
      const isExpired = Boolean(invoice.getExpireDate?.());

      const variantByKey = {
        paid: 'success',
        true: 'warning',
        false: 'muted',
      };

      return {
        id: invoice.id(),
        title: `#${invoice.getNumber()}`,
        subtitle: [
          {
            type: 'status',
            variant: variantByKey[statusKey] ?? variantByKey[String(isExpired)] ?? 'muted',
            label: t(`search:invoices.${statusKey}`, statusKey),
          },
          {
            type: 'text',
            label: formatPrice(invoice.getTotalAmount()),
          },
        ],
        link: `/invoices/${invoice.id()}`,
      };
    },
  },
};
